// Загрузка партии: PGN/список ходов → дерево + сидинг оценок [%eval].
// Порт из app.js v3 (~1909–2076), без UI и сессий — чистое построение.
import { Chess, type Move } from 'chess.js';
import type { Game, GameMeta, GameNode } from './types';
import { appendChild, nodeFromPos, rememberPath } from './tree';
import { applyLooseMove } from './move-detect';
import { EvalStore, evalKey } from './evals';

export interface PageMeta extends GameMeta {
  [key: string]: string | undefined;
}

export function parseEvalCp(v: string | null | undefined): number | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (s[0] === '#') {
    const mate = parseInt(s.slice(1), 10);
    return mate >= 0 ? 2000 : -2000;
  }
  const pawns = parseFloat(s);
  return Number.isFinite(pawns) ? Math.round(pawns * 100) : null;
}

/** Оценки по порядку полуходов — только если есть на КАЖДЫЙ полуход. */
export function extractEvals(pgn: string, n: number): number[] | null {
  const re = /\[%eval (#?[+-]?[\d.]+)\]/g;
  const out: number[] = [];
  let m;
  while ((m = re.exec(pgn))) {
    const cp = parseEvalCp(m[1]);
    if (cp != null) out.push(cp);
  }
  return out.length === n ? out : null;
}

/** Оценки по FEN из комментариев chess.js (надёжнее индексной серии при RAV). */
export function extractFenEvals(chess: Chess): Map<string, number> {
  const out = new Map<string, number>();
  const re = /\[%eval (#?[+-]?[\d.]+)\]/;
  for (const { fen, comment } of chess.getComments()) {
    const m = re.exec(comment || '');
    if (!m) continue;
    const cp = parseEvalCp(m[1]);
    if (cp != null) out.set(evalKey(fen), cp);
  }
  return out;
}

function metaVal(meta: Record<string, unknown> | null | undefined, key: string): string {
  return meta && meta[key] != null ? String(meta[key]).trim() : '';
}

function normalizeMetaDate(v: unknown): string {
  const s = String(v || '').trim();
  if (!s || /\?/.test(s)) return '';
  const m = s.match(/(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/);
  if (m) return `${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}`;
  return s.replace(/\./g, '-');
}

export function pgnMetaFromHeader(h: Record<string, string | undefined> | null): GameMeta | null {
  if (!h) return null;
  return {
    white: h.White || '',
    black: h.Black || '',
    whiteElo: h.WhiteElo || '',
    blackElo: h.BlackElo || '',
    date: (h.Date || h.UTCDate || '').replace(/\./g, '-').replace(/-\?\?/g, ''),
    result: h.Result && h.Result !== '*' ? h.Result : '',
    event: h.Event || '',
    eco: h.ECO || '',
    opening: h.Opening || '',
    timeControl: h.TimeControl || '',
    site: h.Site || '',
  };
}

/** Дополнить мету PGN данными со страницы (PGN-шапка главнее). */
export function mergePageMeta(meta: GameMeta | null, pageMeta: PageMeta | null): GameMeta | null {
  const out: Record<string, string | undefined> = { ...(meta || {}) };
  if (!pageMeta) return meta || null;
  for (const key of ['white', 'black', 'whiteElo', 'blackElo', 'result', 'event', 'eco', 'opening', 'timeControl', 'site']) {
    const v = metaVal(pageMeta, key);
    if (!metaVal(out, key) && v) out[key] = v;
  }
  const d = normalizeMetaDate(pageMeta.date);
  if (!metaVal(out, 'date') && d) out.date = d;
  return Object.values(out).some((v) => String(v || '').trim()) ? (out as GameMeta) : null;
}

/** «Белые (Elo) — Чёрные (Elo) · Дата · Результат». */
export function labelFromMeta(meta: GameMeta | null): string {
  if (!meta) return '';
  const elo = (x?: string) => (x ? ` (${x})` : '');
  const players =
    meta.white || meta.black ? `${meta.white || '?'}${elo(meta.whiteElo)} — ${meta.black || '?'}${elo(meta.blackElo)}` : '';
  const date = normalizeMetaDate(meta.date);
  const res = meta.result && meta.result !== '*' ? meta.result : '';
  return [players, date, res].filter(Boolean).join(' · ');
}

export function historyFromSans(sans: string[]): Move[] {
  const c = new Chess();
  const hist: Move[] = [];
  for (const raw of sans) {
    const mv = applyLooseMove(c, raw);
    if (!mv) break; // на первом непонятном ходе останавливаемся
    hist.push(mv);
  }
  return hist;
}

export interface LoadedGame {
  game: Game;
  /** Сколько полуходов реально легло в дерево. */
  plies: number;
  /** Сколько оценок засеяно из [%eval]. */
  seededEvals: number;
}

export class PgnParseError extends Error {}

/** Построить партию из истории chess.js (m.before/m.after — полные FEN). */
export function buildGameFromHistory(
  hist: Move[],
  meta: GameMeta | null,
  evals: EvalStore,
  opts: { indexEvals?: number[] | null; fenEvals?: Map<string, number> | null; id?: string } = {},
): LoadedGame {
  if (!hist.length) throw new PgnParseError('no_moves');
  const first = hist[0]!;
  const rp = first.before.trim().split(/\s+/);
  const root = nodeFromPos(
    { placement: rp[0]!, turn: rp[1] === 'b' ? 'b' : 'w', fen: first.before },
    null,
    parseInt(rp[5]!, 10) || 1,
  );
  let cur: GameNode = root;
  let seeded = 0;
  for (let i = 0; i < hist.length; i++) {
    const m = hist[i]!;
    const a = m.after.trim().split(/\s+/);
    const node = appendChild(cur, {
      placement: a[0]!,
      turn: a[1] === 'b' ? 'b' : 'w',
      fen: m.after,
      san: m.san,
    });
    cur.fwd = node;
    cur = node;
    const cp = opts.fenEvals?.has(evalKey(m.after))
      ? opts.fenEvals.get(evalKey(m.after))!
      : opts.indexEvals && opts.indexEvals[i] != null
        ? opts.indexEvals[i]!
        : null;
    if (cp != null) {
      evals.set(m.after, cp);
      seeded++;
    }
  }
  rememberPath(cur);
  const game: Game = {
    id: opts.id || `g_${Math.random().toString(36).slice(2, 10)}`,
    startFen: first.before,
    root,
    meta: meta || {},
    label: labelFromMeta(meta),
  };
  return { game, plies: hist.length, seededEvals: seeded };
}

/** PGN → партия (дерево + мета + оценки). Бросает PgnParseError. */
export function loadGameFromPgn(pgn: string, pageMeta: PageMeta | null, evals: EvalStore): LoadedGame {
  const c = new Chess();
  try {
    c.loadPgn(pgn, { strict: false });
  } catch (e) {
    throw new PgnParseError(e instanceof Error ? e.message : String(e));
  }
  const hist = c.history({ verbose: true });
  if (!hist.length) throw new PgnParseError('no_moves');
  let meta: GameMeta | null = null;
  try {
    meta = pgnMetaFromHeader(c.header() as Record<string, string | undefined>);
  } catch {
    /* без шапки */
  }
  meta = mergePageMeta(meta, pageMeta);
  const fenEvals = extractFenEvals(c);
  return buildGameFromHistory(hist, meta, evals, {
    fenEvals: fenEvals.size ? fenEvals : null,
    indexEvals: fenEvals.size ? null : extractEvals(pgn, hist.length),
  });
}

/** Список SAN-ходов (со страницы) → партия. */
export function loadGameFromMoves(sans: string[], pageMeta: PageMeta | null, evals: EvalStore): LoadedGame {
  const hist = historyFromSans(sans);
  if (!hist.length) throw new PgnParseError('no_moves');
  return buildGameFromHistory(hist, mergePageMeta(null, pageMeta), evals, {});
}
